import { useState, useEffect, useCallback } from 'react';
import type { InsightObjectGraph } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object-graph.tsx';
import {
	getGraphConfigurationLocalStorage,
	setGraphConfigurationLocalStorage,
	setReferenceCheckedLocalStorage,
} from './storage/index.tsx';
import type { ObjectGraphConfiguration, ReferenceType } from './types.tsx';
import { getReferenceTypes } from './utils.tsx';

const getDefaultConfiguration = () => ({
	// controls how many degrees of separation of nodes to show based on the initial object
	referenceDepth: 1,
	// controls the amount of inbound nodes of a particular object type before merging them as a single node
	maxOfSameType: 10,
});

const getMaxReferenceDepth = (): number => 5;
const getMaxOfSameType = (): number => 50;

export const useObjectGraphConfiguration = (
	objectGraphData?: InsightObjectGraph,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	initialObjectTypeId: string,
) => {
	const [configuration, setConfiguration] = useState<ObjectGraphConfiguration>(() => ({
		...(getGraphConfigurationLocalStorage() ?? getDefaultConfiguration()),
		referenceTypes: [],
	}));

	// re-generate the reference type filters whenever the graph data updates
	useEffect(() => {
		const { nodes = [], edges = [] } = objectGraphData ?? {};
		const referenceTypes = getReferenceTypes({
			// @ts-expect-error - TS2322 - Type 'Node[]' is not assignable to type 'ReactNode[]'.
			nodes,
			edges,
			initialObjectTypeId,
			referenceDepth: configuration.referenceDepth,
		});
		setConfiguration((config) => ({ ...config, referenceTypes }));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [configuration.referenceDepth, objectGraphData]); // we don't want to listen for changes to initialObjectTypeId here

	const updateReferenceTypes = useCallback(
		(updated: ReferenceType) => {
			setReferenceCheckedLocalStorage({
				objectTypeId: initialObjectTypeId,
				referenceLabel: updated.label,
				updatedValue: updated.checked,
			});
			setConfiguration((config) => ({
				...config,
				referenceTypes: config.referenceTypes.map((r) => (r.label === updated.label ? updated : r)),
			}));
		},
		[initialObjectTypeId],
	);

	const updateReferenceDepth = useCallback(
		(updatedReferenceDepth: number) => {
			if (updatedReferenceDepth >= 1 && updatedReferenceDepth <= getMaxReferenceDepth()) {
				setGraphConfigurationLocalStorage({
					referenceDepth: updatedReferenceDepth,
					maxOfSameType: configuration.maxOfSameType,
				});
				setConfiguration((config) => ({
					...config,
					referenceDepth: updatedReferenceDepth,
				}));
			}
		},
		[configuration],
	);

	const updateMaxOfSameType = useCallback(
		(updatedMaxOfSameType: number) => {
			if (updatedMaxOfSameType >= 1 && updatedMaxOfSameType <= getMaxOfSameType()) {
				setGraphConfigurationLocalStorage({
					referenceDepth: configuration.referenceDepth,
					maxOfSameType: updatedMaxOfSameType,
				});
				setConfiguration((config) => ({
					...config,
					maxOfSameType: updatedMaxOfSameType,
				}));
			}
		},
		[configuration],
	);

	return [
		configuration,
		{ updateReferenceDepth, updateMaxOfSameType, updateReferenceTypes },
	] as const;
};
